/**
 * Sidebar Navigation Component
 * Main navigation menu with groups and items
 */

import { useState, useEffect, useMemo } from 'react'
import { useLocation, useTranslation } from '@spcc/core'
import { isRtlLanguage } from '@spcc/portal-shared'
import { cn } from '@spcc/uikit'
import { selectIsAdmin } from '@spcc/domains/admin-context'
import { useAppSelector } from '@/store/hooks'
import type { SidebarNavProps, MenuGroup } from '../types'
import {
  MAIN_MENU_ITEMS,
  MENU_GROUPS,
  SECONDARY_MENU_ITEMS,
  DEFAULT_EXPANDED_MENUS,
} from '../constants'
import { NavItem } from './nav-item'
import { NavGroup } from './nav-group'

// ============================================
// Helper Functions
// ============================================

function isPathActive(currentPath: string, itemPath: string): boolean {
  return currentPath === itemPath
}

function filterMenuGroupsByAdmin(groups: MenuGroup[], isAdmin: boolean): MenuGroup[] {
  return groups.filter((group) => !group.isAdminOnly || isAdmin)
}

function getActiveGroupIds(groups: MenuGroup[], pathname: string): string[] {
  return groups
    .filter((group) => group.isActivePath(pathname))
    .map((group) => group.id)
}

function mergeExpandedMenus(current: string[], toAdd: string[]): string[] {
  const merged = new Set([...current, ...toAdd])
  return Array.from(merged)
}

function toggleExpandedMenu(current: string[], menuId: string): string[] {
  if (current.includes(menuId)) {
    return current.filter((id) => id !== menuId)
  }
  return [...current, menuId]
}

// ============================================
// Component
// ============================================

export function SidebarNav({ collapsed, onNavigate }: SidebarNavProps) {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const isRTL = isRtlLanguage(i18n.language)
  const isAdmin = useAppSelector(selectIsAdmin)
  const [expandedMenus, setExpandedMenus] = useState<string[]>(DEFAULT_EXPANDED_MENUS)

  // Filter menu groups based on admin status
  const visibleMenuGroups = useMemo(
    () => filterMenuGroupsByAdmin(MENU_GROUPS, isAdmin),
    [isAdmin]
  )

  // Auto-expand active menu groups
  useEffect(() => {
    if (collapsed) return

    const activeGroupIds = getActiveGroupIds(visibleMenuGroups, location.pathname)
    if (activeGroupIds.length > 0) {
      setExpandedMenus((prev) => mergeExpandedMenus(prev, activeGroupIds))
    }
  }, [location.pathname, collapsed, visibleMenuGroups])

  const handleToggleMenu = (menuId: string) => {
    setExpandedMenus((prev) => toggleExpandedMenu(prev, menuId))
  }

  return (
    <nav className={cn('flex-1 overflow-y-auto transition-[padding,gap] duration-300', collapsed ? 'p-2' : 'p-4')}>
      {!collapsed && (
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 mb-3">
          {t('sidebar.menu')}
        </p>
      )}

      <ul className="space-y-1.5">
        {/* Main Menu Items */}
        {MAIN_MENU_ITEMS.map((item) => (
          <li key={item.to}>
            <NavItem
              item={item}
              collapsed={collapsed}
              isActive={isPathActive(location.pathname, item.to)}
              isRTL={isRTL}
              onNavigate={onNavigate}
            />
          </li>
        ))}

        {/* Menu Groups */}
        {visibleMenuGroups.map((group) => (
          <li key={group.id}>
            <NavGroup
              group={group}
              collapsed={collapsed}
              isExpanded={expandedMenus.includes(group.id)}
              isActive={group.isActivePath(location.pathname)}
              isRTL={isRTL}
              onToggle={() => handleToggleMenu(group.id)}
              onNavigate={onNavigate}
              currentPath={location.pathname}
            />
          </li>
        ))}

        {/* Secondary Menu Items */}
        {SECONDARY_MENU_ITEMS.map((item) => (
          <li key={item.to}>
            <NavItem
              item={item}
              collapsed={collapsed}
              isActive={isPathActive(location.pathname, item.to)}
              isRTL={isRTL}
              onNavigate={onNavigate}
            />
          </li>
        ))}
      </ul>
    </nav>
  )
}
